/* MySchoolRx — Privacy Notice. Operated by 589 Labs LLC.
   Contains only verifiable/general statements; no invented hosting locations,
   retention periods, certifications, or internal verification notes. */
import React from "react";
import { PublicPageShell, BLUE } from "./publicChrome";

const EFFECTIVE_DATE = "June 2026";

const Wrap = ({ children }) => (
  <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">{children}</section>
);
const H2 = ({ children }) => (
  <h2 className="mt-10 text-[24px] sm:text-[26px] font-bold text-[#111827] tracking-tight">{children}</h2>
);
const P = ({ children }) => (
  <p className="mt-4 text-[17px] leading-relaxed text-[#4b5563]">{children}</p>
);
const Bullets = ({ items }) => (
  <ul className="mt-4 space-y-2">
    {items.map((t, i) => (
      <li key={i} className="flex items-start gap-3 text-[17px] leading-relaxed text-[#4b5563]">
        <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: BLUE }} />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const MySchoolRxPrivacy = () => (
  <PublicPageShell
    title="MySchoolRx Privacy Notice | 589 Labs LLC"
    description="How 589 Labs LLC, the operator of MySchoolRx, collects, uses, and handles information through the MySchoolRx website and MySchoolRx platform."
    canonicalPath="/privacy"
    testid="privacy-page"
  >
    <div className="border-b border-[#e5e7eb] bg-[#f9fbfc]">
      <Wrap>
        <div className="py-12 sm:py-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#111827] tracking-tight leading-[1.08]" data-testid="privacy-title">
            MySchoolRx Privacy Notice
          </h1>
          <p className="mt-4 text-[16px] text-[#6b7280]" data-testid="privacy-effective-date">Effective date: {EFFECTIVE_DATE}</p>
        </div>
      </Wrap>
    </div>

    <Wrap>
      <div className="py-10 sm:py-14 pb-16">
        <H2>About this notice</H2>
        <P>
          This notice explains how 589 Labs LLC, the operator of MySchoolRx, collects, uses, and handles information
          through the MySchoolRx website and MySchoolRx platform.
        </P>

        <H2>Information we collect</H2>
        <P>Depending on how you use MySchoolRx, we may collect:</P>
        <Bullets items={[
          "Contact and account information, such as your name, email address, organization, and role.",
          "Information you submit through contact forms or support requests.",
          "Diagnostic responses and supporting information submitted by authorized users.",
          "Technical information needed to operate the service, such as browser information and application activity.",
        ]} />

        <H2>How we use information</H2>
        <P>
          We use information to provide and support the service, manage authorized access, process Diagnostic responses,
          generate reports, respond to inquiries, and maintain the platform.
        </P>

        <H2>School information and student data</H2>
        <P>
          MySchoolRx is intended for organizational assessment. Users should avoid submitting student-identifiable
          information unless its use has been expressly authorized and appropriate arrangements are in place.
        </P>
        <P>
          School information may be subject to applicable education privacy requirements and agreements. This notice does
          not itself establish FERPA compliance or replace a required school data agreement.
        </P>

        <H2>Access and sharing</H2>
        <P>Access to platform information depends on authorized roles and organizational relationships.</P>
        <P>
          Where a school assessment and a Portfolio assessment both exist, each is associated with its respective
          organization and is handled as a distinct perspective. Service providers that help us operate MySchoolRx may
          process information on our behalf under appropriate obligations.
        </P>

        <H2>Hosting and safeguards</H2>
        <P>
          MySchoolRx operates using third-party cloud infrastructure and applies administrative and technical measures
          intended to protect information. Specific hosting and safeguard details are maintained by 589 Labs LLC and may
          be described in an applicable organizational agreement.
        </P>

        <H2>Retention and deletion</H2>
        <P>
          We retain information for as long as it is needed to provide the service and to meet legal, contractual, and
          operational requirements. Account-closure and deletion requests can be made through the Contact MySchoolRx
          page. Applicable backup or legal-retention requirements may apply.
        </P>

        <H2>Cookies and analytics</H2>
        <P>
          The website and platform use cookies and similar technologies to operate the service and to understand how it
          is used. You can control cookies through your browser settings. The specific analytics tools used and their
          available controls are maintained by 589 Labs LLC.
        </P>

        <H2>Questions and requests</H2>
        <P>
          For privacy questions or requests concerning your information, contact 589 Labs LLC through the{" "}
          <a href="/contact" className="font-semibold hover:underline" style={{ color: BLUE }} data-testid="privacy-contact-link">
            Contact MySchoolRx
          </a>{" "}
          page. We may need to verify your identity and authority before acting on a request.
        </P>

        <H2>Updates</H2>
        <P>
          We may update this notice when our practices change. The effective date identifies the current version.
        </P>

        <p className="mt-10 text-[15px] text-[#6b7280]" data-testid="privacy-operator-line">
          MySchoolRx is operated by 589 Labs LLC.
        </p>
      </div>
    </Wrap>
  </PublicPageShell>
);

export default MySchoolRxPrivacy;
